export const DOCUSIGN_MAX_ATTEMPTS = 'DocusignMaxAttempts';
export const BUSINESS_TYPE = 'BusinessType';
export const BUSINESS_NAME = 'BusinessName';
export const ACCOUNT_SUBSCRIPTION = 'AccountSubscription';
export const AREA_SERVED = 'AreaServed';
export const SERVICEABLE_AREA = 'ServiceableArea';
export const SMS_TRANSACTIONAL_OPT_IN = 'SMSTransactionalOptIn';
export const STRIPE_CUSTOMER_ID = 'StripeCustomerID';
export const EMAIL_TRANSACTIONAL_OPT_IN = 'MarketingConsent';
export const TERMS_CONDITION = 'TermsConditions';
export const PRIVACY_POLICY = 'PrivacyPolicy';
export const SERVICE_QUALITY_WAIVER = 'ServiceQualityWaiver';
export const DEFAULT_IMAGE_CATEGORY = 'DefaultImageCategory';

// Helper to get a field value from CustomFields by code pattern
function getFieldValueFromCustomField(customFields, codePattern) {
    if (!Array.isArray(customFields)) return null;

    const field = customFields.find(field => field.Code.includes(codePattern));
    const value = field?.Values?.[0];
    return value != null ? String(value).trim() : null;
}

// Specific helpers for different custom fields
export function getIsMemberFromCustomField(customFields) {
    const membershipStatus = getFieldValueFromCustomField(customFields, '-MembershipStatus-');
    return membershipStatus && membershipStatus.toLowerCase() === 'active';
}

export function getIsShowMemberFromCustomField(customFields) {
    const showMembershipBadge = getFieldValueFromCustomField(customFields, '-Showmembership-');
    return showMembershipBadge && showMembershipBadge.toLowerCase() === 'true';
}

export function getIsGovIdVerifiedFromCustomField(customFields) {
    const govStatus = getFieldValueFromCustomField(customFields, '-GovernmentID-');
    return govStatus && (govStatus === 'Verified' || govStatus === 'Acknowledged');
}

export function getDocusignMaxAttemptsFromCustomField(customFields) {
    const field = customFields.find(field => field.Code.includes(`-${DOCUSIGN_MAX_ATTEMPTS}-`));

    // If field is found, and if the values are not exceeding 3 attempts
    if (field) {
        const values = field.Values[0];
        const dateArray = values.split(', ').map(date => date.trim());
        return dateArray.length < 3;
    }

    // If the field isn't found, assume no limit or no attempts
    return true;
}

export function getBcaaMemberIdCustomField(customFields) {
    return getFieldValueFromCustomField(customFields, '-BCAAMemberID-');
}

export function getServiceableAreaCustomField(customFields) {
    return getFieldValueFromCustomField(customFields, '-ServiceableArea-');
}

export function getServiceableRadiusCustomField(customFields) {
    return getFieldValueFromCustomField(customFields, '-ServiceableRadius-');
}

export function getBusinessTypeCustomField(customFields) {
    return getFieldValueFromCustomField(customFields, `-${BUSINESS_TYPE}-`);
}

export function getBusinessNameCustomField(customFields) {
    return getFieldValueFromCustomField(customFields, `-${BUSINESS_NAME}-`);
}

export function getBusinessLicenseCustomField(customFields) {
    return getFieldValueFromCustomField(customFields, '-BusinessLicense-');
}
export function getProofOfCertificationsCustomField(customFields) {
    return getFieldValueFromCustomField(customFields, '-ProofOfCertifications-');
}
export function getProofOfBusinessInsuranceCustomField(customFields) {
    return getFieldValueFromCustomField(customFields, '-ProofofBusinessInsurance-');
}
export function getAdditionalDocumentsCustomField(customFields) {
    return getFieldValueFromCustomField(customFields, '-AdditionalDocuments-');
}

export function geCountDocusignMaxAttemptsFromCustomField(customFields) {
    const field = customFields.find(field => field.Code.includes(`-${DOCUSIGN_MAX_ATTEMPTS}-`));

    // If field is found, and if the values are not exceeding 3 attempts
    if (field) {
        const values = field.Values[0];
        const dateArray = values.split(', ').map(date => date.trim());
        return dateArray.length;
    }

    // If the field isn't found, assume no limit or no attempts
    return 0;
}

export function getAccountSubscriptionCustomField(customFields) {
    const field = getFieldValueFromCustomField(customFields, `-${ACCOUNT_SUBSCRIPTION}-`);
    return field ? JSON.parse(field) : null;
}

export function getAreaServedFromCustomField(customFields) {
    return getFieldValueFromCustomField(customFields, `-${AREA_SERVED}-`);
}

export function getSMSTransactionalOptInFromCustomField(customFields) {
    const field = getFieldValueFromCustomField(customFields, `-${SMS_TRANSACTIONAL_OPT_IN}-`);
    return JSON.parse(field);
}

export function getEmailTransactionalOptInFromCustomField(customFields) {
    return getFieldValueFromCustomField(customFields, `-${EMAIL_TRANSACTIONAL_OPT_IN}-`);
}

export function getTCFromCustomField(customFields) {
    return getFieldValueFromCustomField(customFields, `-${TERMS_CONDITION}-`);
}

export function getPrivacyPolicyFromCustomField(customFields) {
    return getFieldValueFromCustomField(customFields, `-${PRIVACY_POLICY}-`);
}

export function getServiceWaiverFromCustomField(customFields) {
    return getFieldValueFromCustomField(customFields, `-${SERVICE_QUALITY_WAIVER}-`);
}

export function getCriminalRecordClearedStatusFromCustomField(customFields) {
    const value = customFields.find(field => field.Code.includes('-CriminalRecordCheck-'))?.Values?.[0];

    if (!value) {
        return false;
    }

    let data;
    try {
        data = JSON.parse(value);
    } catch {
        console.warn('⚠️ Invalid CriminalRecordCheck CF JSON.');
        return false;
    }

    const { OrderStatus, RCMPResultResult, CheckExecutions = [] } = data;
    const allChecksComplete = CheckExecutions.every(c => c.status === 'COMPLETE');

    return (OrderStatus === 'COMPLETE' && allChecksComplete) || RCMPResultResult === 'CLEARED';
}

/**
 * Check if user has used their promotional pricing before
 * @param {Array} customFields - User's custom fields array
 * @returns {boolean} True if user has already used the $1 promo
 */
export function getHasUsedPromoFromCustomField(customFields) {
    const accountSubscription = getAccountSubscriptionCustomField(customFields);
    if (!accountSubscription) return false;

    // Check the direct flag
    if (accountSubscription.hasUsedPromo === true) {
        return true;
    }

    // Check previous subscriptions
    const previousSubs = accountSubscription.previousSubscriptions || [];
    return previousSubs.some(sub => sub.hadPromo === true);
}

/**
 * Check if user's subscription is scheduled for cancellation
 * @param {Array} customFields - User's custom fields array
 * @returns {boolean} True if subscription is set to cancel at period end
 */
export function getIsCancelAtPeriodEndFromCustomField(customFields) {
    const accountSubscription = getAccountSubscriptionCustomField(customFields);
    return accountSubscription?.cancelAtPeriodEnd === true;
}

/**
 * Get subscription cancellation timestamp
 * @param {Array} customFields - User's custom fields array
 * @returns {string|null} ISO timestamp when cancellation was requested
 */
export function getCancelationDateFromCustomField(customFields) {
    const accountSubscription = getAccountSubscriptionCustomField(customFields);
    return accountSubscription?.canceledAt || null;
}

/**
 * Get user's subscription history
 * @param {Array} customFields - User's custom fields array
 * @returns {Array} Array of previous subscription objects
 */
export function getSubscriptionHistoryFromCustomField(customFields) {
    const accountSubscription = getAccountSubscriptionCustomField(customFields);
    return accountSubscription?.previousSubscriptions || [];
}

/**
 * Helper function to update subscription data with cancellation info
 * @param {Object} currentSubscription - Current subscription object
 * @param {Object} cancellationData - Cancellation data to merge
 * @returns {Object} Updated subscription object
 */
export function addCancellationToSubscription(currentSubscription, cancellationData) {
    return {
        ...currentSubscription,
        cancelAtPeriodEnd: true,
        canceledAt: cancellationData.canceledAt || new Date().toISOString(),
        ...cancellationData
    };
}

/**
 * Helper function to move current subscription to history and mark promo as used
 * @param {Object} currentSubscription - Current subscription object
 * @param {Object} endData - Data about how/when subscription ended
 * @returns {Object} Updated subscription structure
 */
export function moveSubscriptionToHistory(currentSubscription, endData) {
    const previousSub = {
        subscriptionId: currentSubscription.subscriptionId,
        canceledAt: currentSubscription.canceledAt,
        endedAt: endData.endedAt || new Date().toISOString(),
        hadPromo: endData.hadPromo || false,
        finalAmount: endData.finalAmount,
        ...endData
    };

    return {
        customerId: currentSubscription.customerId,
        subscriptionId: null, // Clear current subscription
        status: 'canceled',
        hasUsedPromo: true, // Mark as used when subscription ends
        previousSubscriptions: [...(currentSubscription.previousSubscriptions || []), previousSub]
    };
}

export function getDefaultImageCategoryFromCustomFields(customFields) {
    const rawValue = getFieldValueFromCustomField(customFields, `-${DEFAULT_IMAGE_CATEGORY}-`);
    if (!rawValue) return null;

    try {
        return JSON.parse(rawValue);
    } catch (err) {
        console.error('Failed to parse DefaultImageCategory value', err);
        return null;
    }
}

export function getStripeCustomerIdCustomField(customFields) {
    return getFieldValueFromCustomField(customFields, `-${STRIPE_CUSTOMER_ID}-`);
}

export function getGovIdStatusFromCustomField(customFields) {
    return getFieldValueFromCustomField(customFields, '-GovernmentID-');
}

export function getGovIdProgressFromCustomField(customFields) {
    return getFieldValueFromCustomField(customFields, '-GovIdProgress-');
}

export function getEnvelopeIDFromCustomField(customFields) {
    return getFieldValueFromCustomField(customFields, '-EnvelopeID-');
}
